/**
 * Los catálogos del levantamiento de **P&G** — mercados (canal tradicional) y góndola (canal
 * moderno).
 *
 * ⚠️ NO es el levantamiento de SOFTYS. Ese vive en `domain/levantamiento.ts`, sus tablas empiezan
 * con `levantamiento_` y está en pausa desde agosto. El 15-set un agente conectado a la base leyó
 * el estudio equivocado y contestó con total seguridad; una respuesta segura sobre la fuente
 * equivocada es peor que un error, porque nadie la revisa.
 *
 * ## Por qué este archivo existe
 *
 * Los dos formularios son HTML sueltos publicados en Cloudflare
 * (`entregables/pg-bodegas-2026/app/index.html` y `entregables/pg-canal-moderno-2026/app/index.html`),
 * y lo que guardan en el jsonb **pierde información en el camino**:
 *
 * - `ficha.presencia` es un mapa plano `marca → estado`: **no lleva la categoría ni la bandera
 *   P&G**. Las dos hay que rederivarlas contra este catálogo o la pantalla no puede separar «P&G»
 *   de «competencia», que es la única pregunta que el cliente hace.
 * - `ficha.precios[].marca` es un string suelto, sin dueño.
 * - El precio por unidad **no se guarda**: el formulario lo muestra y lo descarta.
 *
 * O sea que sin este archivo el panel puede listar la ficha pero no puede *leerla*.
 *
 * 🔴 **Si alguien cambia una lista del formulario, hay que cambiarla acá.** No hay forma de que el
 * código lo detecte: un valor nuevo llega como texto y se pinta como texto. Lo que sí se nota es
 * lo contrario —un valor que este archivo promete y el formulario dejó de mandar— porque queda en
 * cero para siempre.
 */

// ═══════════════════════════════════════════════════════════════════════════
// MERCADOS · canal tradicional
// ═══════════════════════════════════════════════════════════════════════════

export interface CategoriaMer {
  /** Lo que viaja en `ficha.cats[]` y en `ficha.precios[].cat`. */
  id: string;
  /** El rótulo exacto que ve la auditora en el celular. */
  nom: string;
  /** Las tres que P&G marcó como prioridad. En el celular se rotulan «… foco». */
  foco: boolean;
  pg: readonly string[];
  comp: readonly string[];
}

/** Las ocho categorías, en el orden del catálogo del formulario (el mismo que usa `catsMer()`). */
export const CATS_MER: readonly CategoriaMer[] = [
  { id: 'cabello', nom: 'Cuidado del cabello', foco: true,
    pg: ['Head & Shoulders', 'Pantene', 'Herbal Essences'],
    comp: ['Sedal', 'Savital', 'Dove', 'Clear', 'Elvive', 'Pert', 'Tío Nacho', 'Konzil'] },
  { id: 'detergentes', nom: 'Detergentes', foco: true,
    pg: ['Ariel', 'Ace'],
    comp: ['Bolívar', 'Sapolio', 'Opal', 'Marsella', 'Patito'] },
  { id: 'suavizantes', nom: 'Suavizantes', foco: true,
    pg: ['Downy'],
    comp: ['Suavitel', 'Bolívar', 'Amor', 'Aromatel'] },
  { id: 'afeitado', nom: 'Afeitado', foco: false,
    pg: ['Gillette', 'Venus'],
    comp: ['Schick', 'BIC', 'Lea', 'Brut'] },
  { id: 'bucal', nom: 'Cuidado bucal', foco: false,
    pg: ['Oral-B'],
    comp: ['Colgate', 'Kolynos', 'Dento', 'Sensodyne'] },
  { id: 'desodorantes', nom: 'Desodorantes', foco: false,
    pg: ['Old Spice', 'Secret'],
    comp: ['Rexona', 'Nivea', 'Speed Stick', 'Lady Speed Stick', 'Axe', 'Etiquet'] },
  { id: 'femenina', nom: 'Protección femenina', foco: false,
    pg: ['Always'],
    comp: ['Nosotras', 'Kotex', 'Ladysoft'] },
  { id: 'panales', nom: 'Pañales', foco: false,
    pg: ['Pampers'],
    comp: ['Huggies', 'Babysec'] },
];

export const CAT_POR_ID: Record<string, CategoriaMer> =
  Object.fromEntries(CATS_MER.map((c) => [c.id, c]));

export const etiquetaCat = (id: string): string => CAT_POR_ID[id]?.nom ?? id;

/**
 * 🔴 **`Bolívar` está en DOS categorías** (detergentes y suavizantes) y `ficha.presencia` se
 * indexa **solo por nombre**. Marcar «Tiene» Bolívar en Detergentes lo deja marcado también en
 * Suavizantes, sin aviso, y el formulario no puede distinguirlo.
 *
 * Esto NO se resuelve eligiendo una categoría: se resuelve **diciéndolo**. Toda cuenta de
 * presencia por marca lo atribuye a una sola categoría —si no, la marca se cuenta dos veces y el
 * total de marcas medidas queda inflado— y la pantalla explica por qué.
 *
 * Es el único choque de nombre del catálogo: 49 entradas, 48 nombres distintos.
 */
export const MARCAS_EN_DOS_CATEGORIAS: readonly string[] = ['Bolívar'];

/** Las categorías donde vive una marca. Devuelve 2 para `Bolívar` y 1 para todas las demás. */
export function categoriasDeMarca(marca: string): CategoriaMer[] {
  return CATS_MER.filter((c) => c.pg.includes(marca) || c.comp.includes(marca));
}

/**
 * ¿Es marca de P&G? Sale del catálogo, no de un regex sobre el nombre.
 *
 * El formulario de BODEGAS tiene un `esPG(m)` que es un regex; mercados no lo usa y acá tampoco.
 * Un regex sobre nombres de marca falla en los dos sentidos —no reconoce `Oral-B` y se cuelga de
 * parecidos— y acá el error sería invisible: una marca del lado equivocado cambia el titular del
 * reporte sin que nada se rompa.
 */
export function esMarcaPg(marca: string): boolean {
  return CATS_MER.some((c) => c.pg.includes(marca));
}

/** Todas las marcas del catálogo, sin repetir, por categoría y con P&G antes que competencia. */
export const MARCAS_MER: readonly string[] = (() => {
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const c of CATS_MER) {
    for (const m of [...c.pg, ...c.comp]) if (!vistas.has(m)) { vistas.add(m); out.push(m); }
  }
  return out;
})();

export type PresenciaMer = 'presente' | 'quiebre' | 'no_trabaja';

/**
 * Los tres estados, con el rótulo exacto del celular.
 *
 * `quiebre` va en el medio a propósito, y el propio formulario lo explica: *«Se le acabó no es lo
 * mismo que no la trabaja: la primera es una venta perdida esta semana, la segunda es una marca
 * que nunca entró al puesto.»* Son dos hechos opuestos y se arreglan distinto —uno con una llamada
 * al distribuidor, el otro con una negociación—, así que nunca se suman.
 *
 * ⚠️ **Clave ausente = no se respondió.** El formulario BORRA la clave al desmarcar
 * (`delete f.presencia[m]`), así que «sin responder» no es `''` ni `null`: la marca no está en el
 * objeto. Pintarla como «No la trabaja» sería inventar una respuesta.
 */
export const PRESENCIA_MER: readonly { valor: PresenciaMer; label: string; tono: string }[] = [
  { valor: 'presente', label: 'Tiene', tono: 'bg-green-100 text-green-800' },
  { valor: 'quiebre', label: 'Se le acabó', tono: 'bg-amber-100 text-amber-800' },
  { valor: 'no_trabaja', label: 'No la trabaja', tono: 'bg-gray-100 text-gray-700' },
];

export const etiquetaPresencia = (v: string | null | undefined): string =>
  PRESENCIA_MER.find((p) => p.valor === v)?.label ?? '—';

/** `CFG.MER.PRESENT`. Se guarda el texto literal, no un id. `''` = no se eligió. */
export const PRESENTACIONES_MER: readonly string[] = [
  'Sachet o sobre', 'Unidad suelta', 'Paquete o bolsa',
  'Frasco o envase grande', 'Caja, display o bulto',
];

/** `CFG.MER.MOT_REC` — el porqué de la recomendación. */
export const MOTIVOS_RECOMENDACION_MER: readonly string[] = [
  'Me deja más ganancia', 'Sale más rápido', 'El cliente la pide',
  'Me la bonifican', 'Es la más barata', 'No quiso decir',
];

/**
 * 🔴 Los valores de `recomienda.marca` que **NO son marcas**.
 *
 * La lista que arma el formulario es `marcas.concat(['La más barata','Otra','No contestó'])`, así
 * que `No contestó` es un valor GUARDADO, no una cadena vacía. Contarlo en el share de
 * recomendación lo pone cuarto —arriba de Sedal y de Sapolio— en la tabla que el cliente lee como
 * «qué recomienda el puestero». Hoy son 7 fichas.
 *
 * `La más barata` sí es una respuesta válida, pero es una POLÍTICA de precio y no una marca: va en
 * su propia fila y rotulada como respuesta.
 */
export const RECOMIENDA_NO_ES_MARCA: readonly string[] = ['No contestó', 'La más barata', 'Otra'];
/** El único que además no es respuesta: no entra ni al numerador ni al denominador. */
export const RECOMIENDA_SIN_RESPUESTA = 'No contestó';

/** `CFG.MER.ABASTO` — múltiple. */
export const ABASTO_DONDE: readonly string[] = [
  'Mayorista del mismo mercado', 'Mayorista de otra zona (Mesa Redonda, Caquetá…)',
  'Distribuidor o bróker que lo visita', 'Vendedor de la marca',
  'Makro o supermercado', 'Varios sitios', 'No quiso decir',
];

/** `CFG.MER.FRECUENCIA` — única. */
export const ABASTO_FRECUENCIA: readonly string[] = [
  'Diario', '2 o 3 veces por semana', 'Semanal', 'Quincenal',
  'Cuando se le acaba', 'No quiso decir',
];

/**
 * `abasto.visita` guarda CÓDIGO y no rótulo, y `''` es un cuarto estado real.
 *
 * Es texto y no booleano a propósito: así «todavía no se preguntó» (`''`) queda distinguible de
 * «No quiso decir» (`no_dijo`). Colapsarlos convierte una pregunta sin hacer en una negativa.
 */
export const ABASTO_VISITA: readonly { valor: string; label: string }[] = [
  { valor: 'si', label: 'Sí' },
  { valor: 'no', label: 'No' },
  { valor: 'no_dijo', label: 'No quiso decir' },
];

/** `CFG.MER.CLIENTES` — múltiple. */
export const CLIENTES_MER: readonly string[] = [
  'Bodeguero que revende', 'Submayorista', 'Restaurante u hostal',
  'Consumidor final', 'No sabe',
];

/** `CFG.MER.MOTIVOS_NO` — por qué no se pudo levantar el puesto. */
export const MOTIVOS_NO_MER: readonly string[] = [
  'Estaba cerrado', 'No quiso atender', 'No lo encontré', 'El puesto ya no existe',
];

/** `CFG.MECANICAS` — compartida con bodegas. */
export const MECANICAS_MER: readonly string[] = [
  'Precio rebajado', 'Descuento por llevar 2 o más', 'Pack o combo',
  'Producto de regalo', 'Sorteo o raspa', 'Otra',
];

/** `CFG.MER.DE_QUIEN` — de quién es la promoción. */
export const DE_QUIEN_MER: readonly string[] = ['De P&G', 'De la competencia'];

/**
 * Los DIEZ mercados de la lista cerrada del formulario — **todos de Lima**.
 *
 * 🔴 Esta lista es la causa raíz de las once grafías. La ruta del 11 al 13-set salió a Piura,
 * Chiclayo, Tumbes y Tacna, y **ninguno de esos mercados está acá**: las 100 fichas cayeron en
 * «Otro · lo escribo» y cada auditora escribió el nombre como le salió.
 *
 * Y hay algo peor que escribir mal, que la normalización sola no puede arreglar: **elegir de la
 * lista un mercado que no es**. Siete fichas dicen `Mercado Túpac Amaru` —que está en Comas— y su
 * GPS las pone a 1,1 km de Moshoqueque, en Chiclayo, a unos 800 km de Comas. Por eso las
 * coordenadas de abajo no son decoración: son con lo que se comprueba el rótulo elegido.
 *
 * La cura de raíz es agregar los mercados de provincia al formulario. Esto es la curita.
 */
export const MERCADOS_LIMA: readonly {
  nom: string; zona: string; distrito: string; lat: number; lng: number;
}[] = [
  { nom: 'Mercado Túpac Amaru', zona: 'Lima Norte', distrito: 'Comas', lat: -11.9497, lng: -77.0606 },
  { nom: 'Mercado Unicachi Norte', zona: 'Lima Norte', distrito: 'Comas', lat: -11.9364, lng: -77.0619 },
  { nom: 'Mercado Naranjal', zona: 'Lima Norte', distrito: 'Independencia', lat: -11.9784, lng: -77.0622 },
  { nom: 'Mercado Caquetá', zona: 'Lima Norte', distrito: 'San Martín de Porres', lat: -12.0300, lng: -77.0430 },
  { nom: 'Mercado Central de Lima', zona: 'Lima Centro', distrito: 'Cercado de Lima', lat: -12.0510, lng: -77.0250 },
  { nom: 'Mercado N.º 1 de Surquillo', zona: 'Lima Centro', distrito: 'Surquillo', lat: -12.1128, lng: -77.0216 },
  { nom: 'Gran Mercado Mayorista de Lima', zona: 'Lima Este', distrito: 'Santa Anita', lat: -12.0430, lng: -76.9400 },
  { nom: 'Mercado de Productores de Santa Anita', zona: 'Lima Este', distrito: 'Santa Anita', lat: -12.0470, lng: -76.9460 },
  { nom: 'Mercado Ciudad de Dios', zona: 'Lima Sur', distrito: 'San Juan de Miraflores', lat: -12.1560, lng: -76.9740 },
  { nom: 'Mercado Unicachi Sur', zona: 'Lima Sur', distrito: 'Villa El Salvador', lat: -12.2130, lng: -76.9330 },
];

/** `CFG.MER.AFLUENCIA` y `CFG.MER.FACTOR` — del CIERRE DE JORNADA (`mer_cierres`), no de la ficha. */
export const AFLUENCIA_MER: readonly string[] = ['Alta', 'Normal', 'Baja'];
export const FACTOR_EXTERNO_MER: readonly string[] = [
  'Nada raro', 'Lluvia o garúa', 'Paro o bloqueo', 'Feriado',
  'Fiesta o aniversario del mercado', 'Otro',
];

/**
 * Las banderas que pone `banderasMer()`, con su rótulo y su gravedad.
 *
 * ⚠️ `no_levantado` **corta la evaluación**: una ficha no levantada trae
 * `['sin_gps'?, 'no_levantado']` y nada más. O sea que la AUSENCIA de `sin_precios` en un puesto
 * cerrado no significa que tenga precios: significa que ni se miró. Contar banderas sin saber esto
 * produce «solo 11 puestos sin precios» cuando en realidad son 11 + los 11 cerrados.
 */
export const BANDERAS_MER: Record<string, { label: string; tono: 'aviso' | 'grave' | 'neutro' }> = {
  sin_gps: { label: 'sin GPS', tono: 'neutro' },
  no_levantado: { label: 'no levantado', tono: 'aviso' },
  sin_categorias: { label: 'sin categorías', tono: 'aviso' },
  sin_foto: { label: 'sin foto', tono: 'neutro' },
  sin_precios: { label: 'sin precios', tono: 'neutro' },
  sin_abasto: { label: 'sin abastecimiento', tono: 'neutro' },
};

// ═══════════════════════════════════════════════════════════════════════════
// CANAL MODERNO · góndola
// ═══════════════════════════════════════════════════════════════════════════

export interface GondolaCat {
  id: string;
  nom: string;
  /** El orden IMPORTA: el dígito `d` de la grilla es `marcas[d-1]`. */
  marcas: readonly string[];
  /** Cuáles de `marcas` son de P&G. Ver `participacionPg` en `gondola.ts`. */
  pg: readonly string[];
  /** Los chips de «qué productos están», agrupados como los agrupa el formulario. */
  grupos: readonly { titulo: string; items: readonly string[] }[];
}

/**
 * Las DOS góndolas aprobadas el 28-ago. Todo lo demás quedó fuera del alcance.
 *
 * 🔴 **`espacio.marcas[]` y las claves de `items{}` NO son el mismo universo.** La grilla mide
 * ESPACIO con 5 agregados; los chips miden PRESENCIA con ~15 SKU. Unirlos por nombre falla:
 * `Bolívar` es el dígito 3 de la grilla **y** un chip de «las demás marcas», y no son la misma
 * medición.
 */
export const GONDOLAS: readonly GondolaCat[] = [
  {
    id: 'afeitado', nom: 'Afeitado',
    marcas: ['Gillette', 'Venus', 'Schick', 'Otras marcas', 'Marca del súper'],
    pg: ['Gillette', 'Venus'],
    grupos: [
      { titulo: 'Gillette', items: [
        'Gillette Desechable Prestobarba', 'Gillette Mango Mach3', 'Gillette Mango Fusion5',
        'Gillette Cartuchos Mach3', 'Gillette Cartuchos Fusion5', 'Gillette Espuma o gel',
        'Gillette Loción after shave'] },
      { titulo: 'Venus', items: [
        'Venus Desechable', 'Venus Mango', 'Venus Cartuchos', 'Venus Crema de afeitar'] },
      { titulo: 'Schick · el competidor directo', items: [
        'Schick Desechable', 'Schick Mango y cartuchos', 'Schick Espuma o gel'] },
      { titulo: 'Las demás marcas', items: ['BIC', 'Nivea Men', 'Lea', 'Marca del súper'] },
    ],
  },
  {
    id: 'suavizantes', nom: 'Suavizantes',
    marcas: ['Downy', 'Suavitel', 'Bolívar', 'Otras marcas', 'Marca del súper'],
    pg: ['Downy'],
    grupos: [
      { titulo: 'Downy', items: [
        'Downy Floral', 'Downy Amanecer', 'Downy Exótico', 'Downy Fresh',
        'Downy Pureza Silvestre', 'Downy Sachet'] },
      { titulo: 'Suavitel · el competidor directo', items: [
        'Suavitel Complete (concentrado)', 'Suavitel Cuidado Superior (diluido)'] },
      // `Dest` está así, literal, en el formulario. Se ve truncado y NO se corrige acá: si la
      // pantalla lo rotulara distinto, el chip del celular y el del panel dejarían de ser el mismo
      // dato y nadie podría cruzarlos.
      { titulo: 'Las demás marcas', items: ['Bolívar', 'Amor', 'Dest', 'Marca del súper'] },
    ],
  },
];

export const GONDOLA_POR_ID: Record<string, GondolaCat> =
  Object.fromEntries(GONDOLAS.map((g) => [g.id, g]));

/** Los seis colores del pincel de la grilla, en el orden de `marcas[]`. */
export const COLORES_GONDOLA: readonly string[] =
  ['#1d4ed8', '#0B6B5E', '#A65A0B', '#7A5AA6', '#B33429', '#8A959A'];

/**
 * Cómo se llama cada repisa según cuántas tenga el mueble. Del formulario, verbatim.
 *
 * ⚠️ El «a los ojos» repetido en la de 6 **está así en el formulario** y se replica: una góndola de
 * seis repisas tiene DOS niveles a la altura de los ojos. Por eso el indicador del nivel de los
 * ojos suma las dos filas, y por eso la de 3 —que no tiene ninguna— queda fuera del indicador en
 * vez de asignarle una fila cualquiera. Ver `filasOjos` en `gondola.ts`.
 */
export const NIVELES_ETIQUETAS: Record<number, readonly string[]> = {
  3: ['arriba', 'a la mano', 'abajo'],
  4: ['sobre la cabeza', 'a los ojos', 'a la mano', 'piso'],
  5: ['sobre la cabeza', 'a los ojos', 'a la mano', 'rodilla', 'piso'],
  6: ['sobre la cabeza', 'a los ojos', 'a los ojos', 'a la mano', 'rodilla', 'piso'],
};

export const REVISADA_MOD: readonly string[] = ['Sí, la revisé toda', 'No pude terminar'];
export const CARRO_MOD: readonly string[] = ['No', 'Sí'];
export const COND_PRECIO_MOD: readonly string[] = [
  'Oferta directa', 'Tarjeta Oh!', 'Tarjeta Cencosud', 'Tarjeta CMR', 'App', 'Llevando 2 o más',
];
export const MOTIVO_CHOQUE_MOD: readonly string[] = [
  'La competencia tiene oferta y nosotros no', 'Están al lado y el precio es muy distinto',
  'Nuestra oferta contra la de ellos', 'Otro motivo',
];
/** ⚠️ El quinto valor lleva un `·` ADENTRO. No usar `·` para separar una lista de mecánicas. */
export const MECANICAS_MOD: readonly string[] = [
  'Descuento en soles', 'Descuento en %', 'Precio con tarjeta de la cadena',
  'Descuento en la 2da unidad', 'Lleva 2 paga 1 · 3 paga 2', 'Pack con producto de regalo',
  'Cupón de la app', 'Otra',
];
export const TARJETAS_MOD: readonly string[] = ['No', 'Oh!', 'Cencosud', 'CMR', 'App de la cadena'];
export const POP_TIPOS_MOD: readonly string[] = [
  'Cintillo de góndola', 'Stopper', 'Colgante del techo', 'Vinil de piso',
  'Hablador o cartel', 'Exhibidor de marca', 'Cabecera decorada', 'Isla o pallet vestido',
];
export const IMPULSADORA_MOD: readonly string[] = ['No hay', 'De P&G', 'De la competencia'];
export const EXHIBICION_TIPO_MOD: readonly string[] = [
  'Cabecera', 'Isla', 'Exhibidor de marca', 'Canastilla en caja', 'Pallet',
];
export const EXHIBICION_UBIC_MOD: readonly string[] = [
  'Entrada', 'Pasillo central', 'Perímetro', 'Cajas',
];
export const PUBLICIDAD_CAMINO_MOD: readonly string[] = ['No vi nada', 'Sí, vi'];
export const COMPLETO_MOD: readonly string[] = ['Sí, las dos', 'Solo una', 'Ninguna'];
/**
 * ⚠️ `Me pidieron no fotografiar` **no es una falla**: es dato comercial —una tienda que no deja
 * levantar—. Contarlo junto con `Falla del celular` mezcla un problema de acceso con uno de equipo,
 * y el primero se resuelve hablando con la cadena.
 */
export const SIN_FOTO_MOD: readonly string[] = [
  'Tomé todas', 'Preferí no insistir', 'Me pidieron no fotografiar', 'Falla del celular',
];

export const BANDERAS_MOD: Record<string, { label: string; tono: 'aviso' | 'grave' | 'neutro' }> = {
  sin_gps: { label: 'sin GPS', tono: 'neutro' },
  muy_rapida: { label: 'muy rápida', tono: 'aviso' },
  tienda_fuera_de_maestra: { label: 'tienda escrita a mano', tono: 'aviso' },
  sin_espacio_afeitado: { label: 'sin espacio · afeitado', tono: 'aviso' },
  sin_espacio_suavizantes: { label: 'sin espacio · suavizantes', tono: 'aviso' },
};

export const etiquetaBandera = (
  f: string, dic: Record<string, { label: string; tono: 'aviso' | 'grave' | 'neutro' }>,
): { label: string; tono: 'aviso' | 'grave' | 'neutro' } =>
  dic[f] ?? { label: f.replace(/_/g, ' '), tono: 'neutro' };
